import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormLabel,
  RadioGroup,
  Radio,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import { default as GridLegacy } from '@mui/material/Grid';
import {
  Add as AddIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckIcon,
  DateRange as DateRangeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface EmbedSale {
  id: number;
  user_id: number;
  customer_id: string;
  product: string;
  sale_amount: number;
  sale_type: string;
  sale_date: string;
  username: string;
}

interface EmbedSaleProduct {
  id: number;
  name: string;
  category?: string;
}

interface FormData {
  customerId: string;
  product: string;
  saleAmount: string;
  saleType: string;
}

type FilterType = 'week' | 'month' | 'previous-month' | 'year' | 'range';

const FILTER_LABELS: Record<FilterType, string> = {
  week: 'This Week',
  month: 'This Month',
  'previous-month': 'Previous Month',
  year: 'This Year',
  range: 'Date Range',
};

const EmbedSales: React.FC = () => {
  const [sales, setSales] = useState<EmbedSale[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [products, setProducts] = useState<EmbedSaleProduct[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [filter, setFilter] = useState<FilterType>('week');
  const [rangeStart, setRangeStart] = useState<string>('');
  const [rangeEnd, setRangeEnd] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [viewAll, setViewAll] = useState<boolean>(false);
  const [editingSale, setEditingSale] = useState<EmbedSale | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmbedSale | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [currentUser] = useState<any>(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });
  const isSuperuser = !!currentUser && (currentUser.is_master === true || currentUser.role === 'superuser');
  const canModifySale = (sale: EmbedSale): boolean =>
    isSuperuser || sale.user_id === currentUser?.id;

  const [formData, setFormData] = useState<FormData>({
    customerId: '',
    product: '',
    saleAmount: '',
    saleType: 'Sales',
  });

  const fetchSales = useCallback(async () => {
    // For custom ranges, wait until both dates are chosen
    if (filter === 'range' && (!rangeStart || !rangeEnd)) {
      setSales([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await api.getEmbedSales(filter, rangeStart, rangeEnd, viewAll);
      setSales(response.sales || []);
      setTotal(response.total || 0);
    } catch (err: any) {
      console.error('Error fetching sales:', err);
      setError(err.response?.data?.message || 'Failed to fetch sales');
      setSales([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filter, rangeStart, rangeEnd, viewAll]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.getEmbedSalesProducts();
        setProducts(response.products || []);
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };
    fetchProducts();
  }, []);

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: FilterType | null
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };


  const handleDialogOpen = () => {
    setEditingSale(null);
    setFormData({
      customerId: '',
      product: '',
      saleAmount: '',
      saleType: 'Sales',
    });
    setDialogOpen(true);
    setError('');
    setSuccess('');
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingSale(null);
    setError('');
    setSuccess('');
  };

  const handleEditOpen = (sale: EmbedSale) => {
    setEditingSale(sale);
    setFormData({
      customerId: sale.customer_id,
      product: sale.product,
      saleAmount: String(sale.sale_amount),
      saleType: sale.sale_type,
    });
    setError('');
    setSuccess('');
    setDialogOpen(true);
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (field: keyof FormData) => (event: SelectChangeEvent<string>) => {
    handleInputChange(field, event.target.value);
  };

  const handleSubmit = async () => {
    if (!formData.customerId || !formData.product || !formData.saleAmount || !formData.saleType) {
      setError('Please fill in all fields');
      return;
    }

    const amount = Math.round(parseFloat(formData.saleAmount) * 100) / 100;
    if (isNaN(amount) || amount < 0) {
      setError('Please enter a valid sale amount');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      if (editingSale) {
        await api.updateEmbedSale(editingSale.id, {
          customerId: formData.customerId,
          product: formData.product,
          saleAmount: amount,
          saleType: formData.saleType,
        });
        setSuccess('Sale updated successfully!');
      } else {
        await api.createEmbedSale({
          customerId: formData.customerId,
          product: formData.product,
          saleAmount: amount,
          saleType: formData.saleType,
        });
        setSuccess('Sale added successfully!');
      }
      setTimeout(() => {
        handleDialogClose();
        fetchSales();
      }, 1500);
    } catch (err: any) {
      console.error('Error adding sale:', err);
      setError(err.response?.data?.message || (editingSale ? 'Failed to update sale' : 'Failed to add sale'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      setError('');
      await api.deleteEmbedSale(deleteTarget.id);
      setDeleteTarget(null);
      fetchSales();
    } catch (err: any) {
      console.error('Error deleting sale:', err);
      setError(err.response?.data?.message || 'Failed to delete sale');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (e) {
      return dateStr;
    }
  };

  const formatCurrency = (amount: number): string => {
    return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#2e7d32' }}>
            💊 Embed Sales
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Log and track your EmbedCare sales
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleDialogOpen}
          sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
        >
          Add Sale
        </Button>
      </Box>

      {/* Date Filter */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          aria-label="date filter"
        >
          <ToggleButton value="week" aria-label="this week">
            <CalendarIcon sx={{ mr: 1 }} />
            This Week
          </ToggleButton>
          <ToggleButton value="month" aria-label="this month">
            <CalendarIcon sx={{ mr: 1 }} />
            This Month
          </ToggleButton>
          <ToggleButton value="previous-month" aria-label="previous month">
            <CalendarIcon sx={{ mr: 1 }} />
            Previous Month
          </ToggleButton>
          <ToggleButton value="year" aria-label="this year">
            <CalendarIcon sx={{ mr: 1 }} />
            This Year
          </ToggleButton>
          <ToggleButton value="range" aria-label="date range">
            <DateRangeIcon sx={{ mr: 1 }} />
            Date Range
          </ToggleButton>
        </ToggleButtonGroup>

        {filter === 'range' && (
          <>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
            />
          </>
        )}
      </Box>

      {/* Superuser scope toggle */}
      {isSuperuser && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={viewAll ? 'all' : 'mine'}
            exclusive
            onChange={(_e, v) => {
              if (v !== null) setViewAll(v === 'all');
            }}
            size="small"
            color="primary"
            aria-label="sales scope"
          >
            <ToggleButton value="mine">My Sales</ToggleButton>
            <ToggleButton value="all">All Sales</ToggleButton>
          </ToggleButtonGroup>
          {viewAll && (
            <Chip label="Viewing sales from all reps" color="primary" variant="outlined" size="small" />
          )}
        </Box>
      )}

      {/* Sales Table */}
      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error && sales.length === 0 ? (
            <Alert severity="error">{error}</Alert>
          ) : filter === 'range' && (!rangeStart || !rangeEnd) ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                Select a start and end date to view sales
              </Typography>
            </Box>
          ) : sales.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                No sales found for this period
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click "Add Sale" to record your first sale
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
              >
                <Typography variant="h6">Sales ({sales.length})</Typography>
                <Chip
                  label={`${FILTER_LABELS[filter]} Total: ${formatCurrency(total)}`}
                  color="success"
                  variant="outlined"
                />
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Customer ID</strong></TableCell>
                      <TableCell><strong>Product</strong></TableCell>
                      <TableCell align="right"><strong>Sale Amount</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Logged By</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sales.map((sale) => (
                      <TableRow key={sale.id} hover>
                        <TableCell>{formatDate(sale.sale_date)}</TableCell>
                        <TableCell>{sale.customer_id}</TableCell>
                        <TableCell>{sale.product}</TableCell>
                        <TableCell align="right">{formatCurrency(sale.sale_amount)}</TableCell>
                        <TableCell>
                          <Chip
                            label={sale.sale_type}
                            size="small"
                            color={sale.sale_type === 'Sales' ? 'success' : 'info'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{sale.username}</TableCell>
                        <TableCell align="right">
                          {canModifySale(sale) && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEditOpen(sale)}
                                sx={{ color: '#4B88A2', '&:hover': { bgcolor: '#e0f2f7' } }}
                                title="Edit sale"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => setDeleteTarget(sale)}
                                sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                                title="Delete sale"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>


      {/* Add Sale Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSale ? 'Edit Sale' : 'Add New Sale'}</DialogTitle>
        <DialogContent>
          <GridLegacy container spacing={2} sx={{ mt: 1 }}>
            {/* Customer ID */}
            <GridLegacy size={{ xs: 12 }}>
              <TextField
                label="Customer ID"
                fullWidth
                value={formData.customerId}
                onChange={(e) => handleInputChange('customerId', e.target.value)}
                required
              />
            </GridLegacy>

            {/* Product */}
            <GridLegacy size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Product</InputLabel>
                <Select
                  value={formData.product}
                  label="Product"
                  onChange={handleSelectChange('product')}
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.name}>
                      {product.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {products.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  No products configured yet — the product list is being loaded by an administrator.
                </Typography>
              )}
            </GridLegacy>

            {/* Sale Amount */}
            <GridLegacy size={{ xs: 12 }}>
              <TextField
                label="Sale Amount"
                type="number"
                fullWidth
                value={formData.saleAmount}
                onChange={(e) => handleInputChange('saleAmount', e.target.value)}
                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                placeholder="0.00"
                inputProps={{ min: 0, step: '0.01' }}
                required
              />
            </GridLegacy>

            {/* Sale Type */}
            <GridLegacy size={{ xs: 12 }}>
              <FormControl required>
                <FormLabel id="sale-type-label">Sale Type</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="sale-type-label"
                  name="sale-type"
                  value={formData.saleType}
                  onChange={(e) => handleInputChange('saleType', e.target.value)}
                >
                  <FormControlLabel value="Sales" control={<Radio />} label="Sales" />
                  <FormControlLabel value="Customer Service" control={<Radio />} label="Customer Service" />
                </RadioGroup>
              </FormControl>
            </GridLegacy>
          </GridLegacy>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" icon={<CheckIcon />} sx={{ mt: 2 }}>
              {success}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' } }}
          >
            {submitting ? <CircularProgress size={24} /> : editingSale ? 'Save Changes' : 'Add Sale'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Sale</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this sale
            {deleteTarget ? ` (${deleteTarget.product} - ${formatCurrency(deleteTarget.sale_amount)})` : ''}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EmbedSales;

